import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetPostBySlugOptions,
  GetPostBySlugResponse,
  GetPostMetricsResponse,
  GetPostOptions,
  GetTotalPostsOptions,
  GetTotalPostsResponse,
  ListPostsOptions,
  ListPostsResponse,
  Post,
  PostCreatedEnvelope,
  PostDeletedEnvelope,
  PostLikedEnvelope,
  PostQuery,
  PostUnlikedEnvelope,
  PostUpdatedEnvelope,
  PostsQueryBuilder,
  QueryPostCountStatsOptions,
  QueryPostCountStatsResponse,
  QueryPostsOptions,
  QueryPostsResponse,
  getPost as universalGetPost,
  getPostBySlug as universalGetPostBySlug,
  getPostMetrics as universalGetPostMetrics,
  getTotalPosts as universalGetTotalPosts,
  listPosts as universalListPosts,
  queryPostCountStats as universalQueryPostCountStats,
  queryPosts as universalQueryPosts,
  typedQueryPosts as universalTypedQueryPosts,
} from './blog-v3-post-posts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/blog' };

export function queryPostCountStats(
  httpClient: HttpClient
): QueryPostCountStatsSignature {
  return (options?: QueryPostCountStatsOptions) =>
    universalQueryPostCountStats(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPostCountStatsSignature {
  /**
   * Retrieves the number of published posts per month within a specified time range.
   *
   * The time range is set using the `rangeStart` and `months` properties.
   * The time range always starts on the 1st day of the month set in `rangeStart` and
   * includes the number of `months` following `rangeStart`.
   * For example, if `rangeStart` is set to `'2022-03-13'` and `months` is set to `4`,
   * the time range will be from `'2022-03-01'` until `'2022-06-30'`. The time range always ends on the last day of the month.
   * > Note: If there are no published posts in a month within the time range, that month is not included in the response. For example, let's say a blog has `0` posts dated in February 2022. If `rangeStart` is set to `'2022-01-01'` and `months` is set to `3`, the response includes `postCount` values for January and March, but not February.
   * @returns Get Blog Post Count Stats response
   */
  (options?: QueryPostCountStatsOptions): Promise<
    NonNullablePaths<
      QueryPostCountStatsResponse,
      `stats` | `stats.${number}.postCount`,
      4
    >
  >;
}

export function getTotalPosts(httpClient: HttpClient): GetTotalPostsSignature {
  return (options?: GetTotalPostsOptions) =>
    universalGetTotalPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTotalPostsSignature {
  /**
   * Retrieves the total amount of published posts of the blog.
   */
  (options?: GetTotalPostsOptions): Promise<
    NonNullablePaths<GetTotalPostsResponse, `total`, 2>
  >;
}

export function getPost(httpClient: HttpClient): GetPostSignature {
  return (postId: string, options?: GetPostOptions) =>
    universalGetPost(
      postId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPostSignature {
  /**
   * Retrieves a post by the specified ID.
   * @param - Post ID.
   * @returns Retrieved post info.
   */
  (postId: string, options?: GetPostOptions): Promise<
    NonNullablePaths<
      Post,
      | `_id`
      | `title`
      | `excerpt`
      | `slug`
      | `featured`
      | `pinned`
      | `categoryIds`
      | `memberId`
      | `hashtags`
      | `commentingEnabled`
      | `minutesToRead`
      | `tagIds`
      | `relatedPostIds`
      | `pricingPlanIds`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `preview`
      | `moderationDetails.submittedBy`
      | `moderationDetails.status`
      | `media.embedMedia.thumbnail.url`
      | `media.embedMedia.thumbnail.width`
      | `media.embedMedia.thumbnail.height`
      | `media.embedMedia.video.url`
      | `media.embedMedia.video.width`
      | `media.embedMedia.video.height`
      | `media.displayed`
      | `media.custom`
      | `hasUnpublishedChanges`,
      6
    >
  >;
}

export function getPostBySlug(httpClient: HttpClient): GetPostBySlugSignature {
  return (slug: string, options?: GetPostBySlugOptions) =>
    universalGetPostBySlug(
      slug,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPostBySlugSignature {
  /**
   * Retrieves a post with the provided slug.
   *
   * The slug is the end of a post's URL that refers to a specific post.
   * For example, if a post's URL is `https:/example.com/blog/post/my-post-slug`,
   * the slug is `my-post-slug`. The slug is case-sensitive, and is generally derived from the post title,
   * unless specified otherwise.
   * @param - Slug of the post to retrieve.
   */
  (slug: string, options?: GetPostBySlugOptions): Promise<
    NonNullablePaths<
      GetPostBySlugResponse,
      | `post._id`
      | `post.title`
      | `post.excerpt`
      | `post.slug`
      | `post.featured`
      | `post.pinned`
      | `post.categoryIds`
      | `post.memberId`
      | `post.hashtags`
      | `post.commentingEnabled`
      | `post.minutesToRead`
      | `post.tagIds`
      | `post.relatedPostIds`
      | `post.pricingPlanIds`
      | `post.seoData.tags`
      | `post.seoData.tags.${number}.type`
      | `post.seoData.tags.${number}.children`
      | `post.seoData.tags.${number}.custom`
      | `post.seoData.tags.${number}.disabled`
      | `post.seoData.settings.preventAutoRedirect`
      | `post.seoData.settings.keywords`
      | `post.seoData.settings.keywords.${number}.term`
      | `post.seoData.settings.keywords.${number}.isMain`
      | `post.preview`
      | `post.moderationDetails.submittedBy`
      | `post.moderationDetails.status`
      | `post.media.embedMedia.thumbnail.url`
      | `post.media.embedMedia.thumbnail.width`
      | `post.media.embedMedia.thumbnail.height`
      | `post.media.embedMedia.video.url`
      | `post.media.embedMedia.video.width`
      | `post.media.embedMedia.video.height`
      | `post.media.displayed`
      | `post.media.custom`
      | `post.hasUnpublishedChanges`,
      7
    >
  >;
}

export function listPosts(httpClient: HttpClient): ListPostsSignature {
  return (options?: ListPostsOptions) =>
    universalListPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListPostsSignature {
  /**
   * Retrieves a list of up to 100 published posts per request.
   *
   * List Posts runs with these defaults, which you can override:
   * - `firstPublishedDate` is sorted in descending order, with pinned posts first.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   */
  (options?: ListPostsOptions): Promise<
    NonNullablePaths<
      ListPostsResponse,
      | `posts`
      | `posts.${number}._id`
      | `posts.${number}.title`
      | `posts.${number}.excerpt`
      | `posts.${number}.slug`
      | `posts.${number}.featured`
      | `posts.${number}.pinned`
      | `posts.${number}.memberId`
      | `posts.${number}.commentingEnabled`
      | `posts.${number}.minutesToRead`
      | `posts.${number}.seoData.settings.preventAutoRedirect`
      | `posts.${number}.preview`
      | `posts.${number}.moderationDetails.submittedBy`
      | `posts.${number}.moderationDetails.status`
      | `posts.${number}.media.displayed`
      | `posts.${number}.media.custom`
      | `posts.${number}.hasUnpublishedChanges`
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`,
      6
    >
  >;
}

export function queryPosts(httpClient: HttpClient): QueryPostsSignature {
  return (options?: QueryPostsOptions) =>
    universalQueryPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPostsSignature {
  /**
   * Retrieves a list of up to 100 posts, given the provided paging, filtering, and sorting.
   *
   * Query Posts runs with these defaults, which you can override:
   * - `firstPublishedDate` is sorted in descending order, with pinned posts first.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
   * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging), and
   * [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
   */
  (options?: QueryPostsOptions): PostsQueryBuilder;
}

export function typedQueryPosts(
  httpClient: HttpClient
): TypedQueryPostsSignature {
  return (query: PostQuery, options?: QueryPostsOptions) =>
    universalTypedQueryPosts(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPostsSignature {
  /** */
  (query: PostQuery, options?: QueryPostsOptions): Promise<
    NonNullablePaths<
      QueryPostsResponse,
      | `posts`
      | `posts.${number}._id`
      | `posts.${number}.title`
      | `posts.${number}.excerpt`
      | `posts.${number}.slug`
      | `posts.${number}.featured`
      | `posts.${number}.pinned`
      | `posts.${number}.memberId`
      | `posts.${number}.commentingEnabled`
      | `posts.${number}.minutesToRead`
      | `posts.${number}.seoData.settings.preventAutoRedirect`
      | `posts.${number}.preview`
      | `posts.${number}.moderationDetails.submittedBy`
      | `posts.${number}.moderationDetails.status`
      | `posts.${number}.media.displayed`
      | `posts.${number}.media.custom`
      | `posts.${number}.hasUnpublishedChanges`,
      6
    >
  >;
}

export function getPostMetrics(
  httpClient: HttpClient
): GetPostMetricsSignature {
  return (postId: string) =>
    universalGetPostMetrics(
      postId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPostMetricsSignature {
  /**
   * Retrieves a post's metrics.
   *
   * A post's metrics include the comments, likes, and views the post receives.
   * @param - Post ID to retrieve metrics for.
   */
  (postId: string): Promise<
    NonNullablePaths<
      GetPostMetricsResponse,
      `metrics.comments` | `metrics.likes` | `metrics.views`,
      3
    >
  >;
}

export const onPostLiked = EventDefinition(
  'wix.blog.v3.post_liked',
  true,
  (event: PostLikedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<PostLikedEnvelope>();
export const onPostUnliked = EventDefinition(
  'wix.blog.v3.post_unliked',
  true,
  (event: PostUnlikedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<PostUnlikedEnvelope>();
export const onPostCreated = EventDefinition(
  'wix.blog.v3.post_created',
  true,
  (event: PostCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.firstPublishedDate' },
            { path: 'entity.lastPublishedDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'entity.moderationDetails.submittedDate' },
            { path: 'entity.moderationDetails.moderationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.heroImage' },
            { path: 'entity.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTBytesToSDKBytes,
          paths: [{ path: 'entity.richContentCompressed' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.metrics.averageRating' },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.shapeData.shape.duration' },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.richContent.nodes.tocData.fontSize' },
            { path: 'entity.richContent.nodes.tocData.itemSpacing' },
            {
              path: 'entity.richContent.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.media.wixMedia.videoV2' }],
        },
      ])
    )
)<PostCreatedEnvelope>();
export const onPostDeleted = EventDefinition(
  'wix.blog.v3.post_deleted',
  true,
  (event: PostDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.firstPublishedDate' },
            { path: 'entity.lastPublishedDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'entity.moderationDetails.submittedDate' },
            { path: 'entity.moderationDetails.moderationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.heroImage' },
            { path: 'entity.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTBytesToSDKBytes,
          paths: [{ path: 'entity.richContentCompressed' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.metrics.averageRating' },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.shapeData.shape.duration' },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.richContent.nodes.tocData.fontSize' },
            { path: 'entity.richContent.nodes.tocData.itemSpacing' },
            {
              path: 'entity.richContent.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.media.wixMedia.videoV2' }],
        },
      ])
    )
)<PostDeletedEnvelope>();
export const onPostUpdated = EventDefinition(
  'wix.blog.v3.post_updated',
  true,
  (event: PostUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.firstPublishedDate' },
            { path: 'entity.lastPublishedDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'entity.moderationDetails.submittedDate' },
            { path: 'entity.moderationDetails.moderationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.heroImage' },
            { path: 'entity.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTBytesToSDKBytes,
          paths: [{ path: 'entity.richContentCompressed' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.metrics.averageRating' },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.shapeData.shape.duration' },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.richContent.nodes.tocData.fontSize' },
            { path: 'entity.richContent.nodes.tocData.itemSpacing' },
            {
              path: 'entity.richContent.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.media.wixMedia.videoV2' }],
        },
      ])
    )
)<PostUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  AspectRatio,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BannerOrigin,
  BannerPosition,
  BaseEventMetadata,
  BlockquoteData,
  BlogPaging,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkDeletePostsRequest,
  BulkDeletePostsResponse,
  BulkGetPostReactionsRequest,
  BulkGetPostReactionsResponse,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  Category,
  CategoryTranslation,
  CellStyle,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  ConvertDraftJsToRichContentRequest,
  ConvertDraftJsToRichContentResponse,
  ConvertRichContentToDraftJsRequest,
  ConvertRichContentToDraftJsResponse,
  CreateDraftPostFromTemplateRequest,
  CreateDraftPostFromTemplateResponse,
  Crop,
  CursorPaging,
  Cursors,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeletePostRequest,
  DeletePostResponse,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftPost,
  DraftPostTranslation,
  EmbedData,
  EmbedMedia,
  EmbedThumbnail,
  EmbedVideo,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventData,
  EventMetadata,
  Field,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FocalPoint,
  FontFamilyData,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetPostBySlugOptions,
  GetPostBySlugRequest,
  GetPostBySlugResponse,
  GetPostCountPerMonthRequest,
  GetPostCountPerMonthResponse,
  GetPostCountsRequest,
  GetPostCountsResponse,
  GetPostMetricsRequest,
  GetPostMetricsResponse,
  GetPostOptions,
  GetPostRequest,
  GetPostResponse,
  GetPostTemplatesSort,
  GetPostsSort,
  GetTemplateRequest,
  GetTemplateResponse,
  GetTotalLikesPerMemberRequest,
  GetTotalLikesPerMemberResponse,
  GetTotalPostsOptions,
  GetTotalPostsRequest,
  GetTotalPostsResponse,
  GetTotalPublicationsRequest,
  GetTotalPublicationsResponse,
  Gradient,
  GradientType,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  Indentation,
  InitialExpandedItems,
  InitialPostsCopied,
  Item,
  ItemDataOneOf,
  ItemStyle,
  Keyword,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LikePostRequest,
  LikePostResponse,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListDemoPostsRequest,
  ListDemoPostsResponse,
  ListPostsArchiveRequest,
  ListPostsArchiveResponse,
  ListPostsOptions,
  ListPostsRequest,
  ListPostsResponse,
  ListStyle,
  ListTemplatesRequest,
  ListTemplatesResponse,
  ListValue,
  MapData,
  MapSettings,
  MapType,
  Media,
  MediaMediaOneOf,
  MentionData,
  MessageEnvelope,
  MetaData,
  Metadata,
  Metrics,
  ModerationDetails,
  ModerationStatusStatus,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  Order,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Paging,
  PagingMetadataV2,
  ParagraphData,
  PeriodPostCount,
  PeriodPublicationsCount,
  Permissions,
  PinPostRequest,
  PinPostResponse,
  Placement,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  Post,
  PostCategoriesUpdated,
  PostCount,
  PostCountInfo,
  PostCountPerMonth,
  PostCountersUpdated,
  PostCountersUpdatedInitiatorOneOf,
  PostCreatedEnvelope,
  PostDeletedEnvelope,
  PostFieldField,
  PostLiked,
  PostLikedEnvelope,
  PostLikedInitiatorOneOf,
  PostOwnerChanged,
  PostQuerySpec,
  PostTagsUpdated,
  PostTranslation,
  PostUnliked,
  PostUnlikedEnvelope,
  PostUnlikedInitiatorOneOf,
  PostUpdatedEnvelope,
  PostsQueryBuilder,
  PostsQueryResult,
  PricingData,
  QueryPostCountStatsOptions,
  QueryPostCountStatsRequest,
  QueryPostCountStatsResponse,
  QueryPostsOptions,
  QueryPostsRequest,
  QueryPostsResponse,
  QueryPublicationsCountStatsRequest,
  QueryPublicationsCountStatsRequestOrder,
  QueryPublicationsCountStatsResponse,
  Reactions,
  Rel,
  Resizing,
  ResponsivenessBehaviour,
  RestoreInfo,
  RibbonStyles,
  RichContent,
  Scaling,
  ScheduledPostPublished,
  SeoSchema,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  Status,
  Stop,
  Styles,
  StylesBorder,
  StylesPosition,
  TableCellData,
  TableData,
  Tag,
  Target,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TocData,
  Type,
  UnlikePostRequest,
  UnlikePostResponse,
  UnpinPostRequest,
  UnpinPostResponse,
  V1Media,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  VideoResolution,
  ViewMode,
  ViewPostRequest,
  ViewPostResponse,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  WixMedia,
  utils,
} from './blog-v3-post-posts.universal.js';
